import {Button, Dialog, DialogClose, DialogContent} from '@tinct/web-components';
import {Organization} from '@tinct/core-api-models';
import {createConnectedInputHelpers} from '@tinct/web-form';
import {FormProvider} from 'react-hook-form';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Info, X} from 'lucide-react';
import {
  CreateChildOrganizationFormValues,
  useCreateChildOrganizationForm,
} from '../hooks/use-create-child-organization-form';

type CreateChildOrganizationModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agencyId: string;
};

/**
 * Creates a client workspace managed by the current agency. The connected user
 * becomes its admin; the agency's generation/publication quotas are shared with
 * it until it gets a subscription of its own.
 */
export const CreateChildOrganizationModal = ({
  open,
  onOpenChange,
  agencyId,
}: CreateChildOrganizationModalProps) => {
  const navigate = useNavigate();
  const [createdOrganization, setCreatedOrganization] = useState<Organization | null>(null);

  const {form, handleSubmit, isLoading, isSuccess, reset} =
    useCreateChildOrganizationForm({
      agencyId,
      onSuccess: (organization) => setCreatedOrganization(organization),
    });

  const {ControlledTextInput, ControlledCheckbox} =
    createConnectedInputHelpers<CreateChildOrganizationFormValues>();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      reset();
      setCreatedOrganization(null);
    }
    onOpenChange(open);
  };

  const handleOpenWorkspace = () => {
    if (createdOrganization?.id) {
      navigate(`/w/${createdOrganization.id}/dispatch`);
    }
    handleOpenChange(false);
  };

  const isSuccessState = isSuccess && createdOrganization;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex flex-col gap-6 rounded-3xl p-8 shadow-lg sm:max-w-md"
      >
        {isSuccessState ? (
          <>
            <div className="flex flex-col gap-3 text-center">
              <h2 className="font-heading text-2xl font-semibold tracking-tight text-foreground">
                Workspace created!
              </h2>
              <p className="text-base text-muted-foreground">
                {createdOrganization.name} is now managed by your agency. Set up
                its brand to start creating campaigns.
              </p>
            </div>
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={() => handleOpenChange(false)}>
                Close
              </Button>
              <Button onClick={handleOpenWorkspace}>Open workspace</Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start gap-4">
              <h2 className="flex-1 font-heading text-xl font-semibold tracking-tight text-foreground">
                Create a client workspace
              </h2>
              <DialogClose className="shrink-0 opacity-70 transition-opacity hover:opacity-100">
                <X className="size-6" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>

            <FormProvider {...form}>
              <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <ControlledTextInput
                  name="name"
                  label="Workspace name *"
                  placeholder="Client name"
                  direction="vertical"
                />
                <ControlledTextInput
                  name="domain"
                  label="Website domain"
                  placeholder="client.com"
                  direction="vertical"
                  description="Optional — you can run the brand analysis later."
                />
                <ControlledCheckbox
                  name="copyMembers"
                  label="Copy the agency's team into this workspace"
                />

                <div className="flex gap-3 rounded-2xl border border-indigo-100 bg-indigo-50 p-4 text-primary">
                  <Info className="mt-0.5 size-4 shrink-0" />
                  <div className="flex flex-col gap-2 text-sm leading-5">
                    <p className="font-medium">Managed by your agency</p>
                    <p>
                      This workspace uses one of your agency's workspace seats
                      and shares its generation and publication credits.
                    </p>
                  </div>
                </div>

                {form.formState.errors.root && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.root.message}
                  </p>
                )}

                <div className="flex items-center justify-between">
                  <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" isLoading={isLoading}>
                    Create workspace
                  </Button>
                </div>
              </form>
            </FormProvider>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
